<template>
    <v-autocomplete
        v-model="selectedClient"
        :items="clients"
        :item-title="itemText || inputText"
        :item-value="itemValue || 'personId' || inputValue"
        :loading="isLoadingClients || loading"
        hide-no-data
        :label="label"
        :multiple="multiple"
        :chips="multiple"
        :closable-chips="closableChips"
        :return-object="!itemValue"
        variant="outlined"
        :density="dense ? 'compact' : 'comfortable'"
        hide-details
        :readonly="readonly"
        :persistent-placeholder="!disablePersistentPlaceholder"
        bg-color="white"
        :clearable="clearable"
        :disabled="disabled"
        :rules="rules || [requiredRule('select_user')]"
        :menu-icon="clients.length ? '$dropdown' : ''"
        @update:model-value="change"
        @update:search="onInput"
        @blur="onBlur"
    >
        <template v-slot:item="{props, item}">
            <v-list-item
                v-bind="props"
                :title="itemText ? item.raw[itemText] : inputText(item.raw)"
                :subtitle="getSubtitle(item.raw)"
            >
                <template v-slot:title="{title}">
                    <div class="d-flex align-center">
                        <span v-html="getHighlightedTitle(title)" />
                    </div>
                </template>
            </v-list-item>
        </template>
        <template v-slot:prepend-item>
            <slot />
        </template>
    </v-autocomplete>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'

    @Component
    export class ClientAutocomplete extends mixins(FormValidationRulesMixin) {
        @VModel() selectedClient
        @Prop() companyId
        @Prop() limit
        @Prop() label
        @Prop({type: Boolean, default: false}) isTa
        @Prop() itemValue
        @Prop() itemText
        @Prop() rules
        @Prop({default: false, type: Boolean}) disablePersistentPlaceholder
        @Prop({type: Boolean, default: false}) disabled
        @Prop({type: Boolean, default: false}) multiple
        @Prop({type: Boolean, default: false}) outlined
        @Prop({type: Boolean, default: false}) dense
        @Prop({type: Boolean, default: false}) readonly
        @Prop({type: Boolean, default: false}) loading
        @Prop({type: Boolean, default: true}) clearable
        @Prop({type: Boolean, default: false}) closableChips
        @Prop({type: Boolean, default: false}) preloadAll

        clients = []
        isLoadingClients = false
        searchInput = null
        timer = null
        cancelTokenSource = null

        @Emit()
        change(val) {
            return val
        }

        @Emit()
        onBlur() {
            return this.searchInput
        }

        @Watch('companyId')
        async onCompanyIdChange(value) {
            this.clearResults()
            if (value) {
                await this.loadClients()
            }
        }

        async mounted() {
            if (this.preloadAll) {
                await this.loadClients()
            }

            if (this.selectedClient && Object.keys(this.selectedClient)?.length) {
                this.clients = [...this.clients, this.selectedClient]
            }
        }

        getHighlightedTitle(title) {
            const regex = new RegExp(this.searchInput, 'i')
            return title.replace(regex, '<strong>$&</strong>')
        }

        async loadClients() {
            try {
                this.isLoadingClients = true

                if (this.cancelTokenSource) this.cancelTokenSource.cancel('Operation canceled by the user')
                this.cancelTokenSource = this.$axios.CancelToken.source()

                const data = await this.$api[this.isTa ? 'persons' : 'suggestPrivateClients'].get(
                    {
                        pattern: this.searchInput,
                        active: true,
                        companyId: this.companyId,
                        limit: this.limit,
                    },
                    this.cancelTokenSource.token
                )

                if (data) {
                    const personArray = data[this.isTa ? 'users' : 'persons']
                    personArray.sort((a, b) =>
                        `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
                            `${b.firstName || ''} ${b.lastName || ''}`
                        )
                    )
                    this.clients = personArray
                    if (this.itemValue) {
                        this.clients = this.clients.filter(el => el[this.itemValue])
                    }
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.isLoadingClients = false
            }
        }

        async onInput(val) {
            await this.$nextTick()
            if (this.selectedClient && !this.multiple) return
            this.searchInput = val
            this.clearResults()
            if (val?.length && !this.preloadAll) {
                await this.loadClients()
            }
        }

        clearResults() {
            if (!this.multiple) {
                this.selectedClient = null
                this.clients = []
            }
        }

        inputText(val) {
            if (!val) return ''
            const {firstName, firstNameLocalized, lastName, lastNameLocalized, prefix} = val
            const fullName = [prefix, firstName || firstNameLocalized, lastName || lastNameLocalized].filter(Boolean)
            return fullName.join(' ')
        }

        getSubtitle(val) {
            if (!val) return ''
            const {email, contactPhone, mobilePhone, addressLine} = val
            const contacts = [email, contactPhone || mobilePhone, addressLine].filter(Boolean)
            return contacts.length ? `${contacts.join('; ')}` : ''
        }

        inputValue(val) {
            return val
        }
    }

    export default ClientAutocomplete
</script>
